import { type FunctionComponent } from 'react';
import { NO_IMAGE_URL } from '../../../constants/images';
import { type CloudinaryOptions, generateCloudinaryUrl } from '../../../helpers/cloudinary-helper/cloudinary-helper';
import { type Variation, generateDimensionAttributes } from '../../../helpers/images/images.helper';
import { type ActualImageProps } from '../image.types';

export type PictureImageProps = {
	imageProps?: ActualImageProps;
	className?: string;
	description: string;
	draggable?: boolean;
	ignoreChromatic?: boolean;
	loading?: 'eager' | 'lazy';
	options: CloudinaryOptions;
	publicID?: string;
	useDimensions?: boolean;
	artDirectionVariations?: Variation[];
};

/**
 * A base component built on the <picture> element that supports art direction
 * via <source> elements and uses Cloudinary's progressive image loading (fl_progressive)
 * instead of react-lazy-image placeholders.
 */
export const PictureImage: FunctionComponent<PictureImageProps> = ({
	imageProps = {},
	className,
	description,
	draggable,
	ignoreChromatic = false,
	loading = 'lazy',
	options,
	publicID = NO_IMAGE_URL,
	useDimensions = true,
	artDirectionVariations = []
}) => {
	const primaryOptions: CloudinaryOptions = { ...options, type: options?.type || 'private', progressive: true };
	const { height = 0, width = 0 } = primaryOptions;
	const src = generateCloudinaryUrl(publicID, primaryOptions);
	const dimensions = useDimensions && artDirectionVariations.length === 0 ? generateDimensionAttributes(width, height) : {};

	return (
		<picture>
			{artDirectionVariations.map((variation, index) => {
				const { mediaCondition, options: variationOptions, publicID: variationPublicID } = variation;
				const { maxWidth, unit = 'px' } = mediaCondition;
				const srcSet = generateCloudinaryUrl(variationPublicID ?? publicID, { ...variationOptions, progressive: true });

				return (
					<source
						key={index}
						media={`(max-width: ${maxWidth}${unit})`}
						srcSet={srcSet}
						data-testid={`picture-source-${maxWidth}`}
					/>
				);
			})}
			<img
				alt={description}
				className={className}
				data-chromatic={ignoreChromatic ? 'ignore' : undefined}
				draggable={draggable}
				loading={loading}
				src={src}
				{...dimensions}
				{...imageProps}
			/>
		</picture>
	);
};
